/**
 * Excel & CSV Export Utility
 * Exports results in the exact format of Export Format.xlsx
 * 92 columns: fixed fields + PROPERTY1..30 / VALUE_OF_PROPERTY1..30
 */
import * as XLSX from 'xlsx';
import { injectManufacturerMeta } from './enrichmentUtils';
import { getDescriptions } from './descriptions';

// ── Fixed column headers (A → AF, columns 1–32) ─────────────────────
const FIXED_HEADERS = [
    'Part Number',
    'Description 1',
    'Description 2',
    'given_Supplier Name',
    'given_Supplier Partnumber',
    'given_Supplier Name2',
    'given_Supplier Partnumber2',
    'given_Manufacture Name1',
    'given_Manufacturers part number1',
    'given_Manufacturers part number2',
    'given_Model',
    'NOUN/MODIFIER',
    'CLEANSED/ENRICHED',
    'BRAND NAME',
    'MANUFACTURER NAME 1',
    'MANUFACTURER PART NUMBER 1',
    'MANUFACTURER URL 1',
    'MANUFACTURER NAME 2',
    'MANUFACTURER PART NUMBER 2',
    'MANUFACTURER URL 2',
    'MANUFACTURER NAME 3',
    'MANUFACTURER PART NUMBER 3',
    'MANUFACTURER URL 3',
    'VENDOR NAME',
    'VENDOR PART NUMBER',
    'VENDOR URL',
    'VENDOR NAME 2',
    'VENDOR PART NUMBER 2',
    'VENDOR URL 2',
    'VENDOR NAME 3',
    'VENDOR PART NUMBER 3',
    'VENDOR URL 3',
    'VENDOR NAME 4',
    'VENDOR PART NUMBER 4',
    'VENDOR URL 4',
    'VENDOR NAME 5',
    'VENDOR PART NUMBER 5',
    'VENDOR URL 5',
    'UNUSED_TEXT',
];

// Property pair columns (AG → CN, columns 33–92)
const MAX_PROPERTIES = 30;

/**
 * Parse AI output into a flat object, handling strings, arrays, double-stringify
 */
function parseAIOutput(chatgptOutput) {
    if (!chatgptOutput) return {};
    let raw = chatgptOutput;
    try {
        // If it's a string, strip markdown code fences and thinking blocks first
        if (typeof raw === 'string') {
            // Remove <think>...</think> blocks
            raw = raw.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
            // Remove markdown code fences: ```json ... ``` or ``` ... ```
            // Only apply if the string actually contains triple backticks
            if (raw.includes('```')) {
                raw = raw.replace(/^[\s\S]*?```(?:json)?\s*\n?/i, '').replace(/\n?\s*```[\s\S]*$/i, '').trim();
            }
        }
        for (let i = 0; i < 5; i++) {
            if (typeof raw === 'string') {
                try { raw = JSON.parse(raw); } catch {
                    // Try to extract JSON object from the string
                    const jsonMatch = raw.match(/\{[\s\S]*\}/);
                    if (jsonMatch) {
                        try { raw = JSON.parse(jsonMatch[0]); } catch { break; }
                    } else {
                        break;
                    }
                }
            }
            if (Array.isArray(raw)) { raw = raw[0]; continue; }
            if (raw && typeof raw === 'object' && !Array.isArray(raw)) break;
        }
    } catch { /* ignore */ }

    const obj = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};

    // Flatten nested objects (e.g. ATTRIBUTES: {TYPE: "..."}, META: {...})
    // into a single-level key-value map for export
    return flattenAIObject(obj);
}

/**
 * Flatten a nested AI output object into a single-level key-value map.
 * - If a value is a plain object, its own keys are merged into the top level.
 * - If a value is an array of objects, each object's keys are merged (numbered if conflicts).
 * - Scalar values are kept as-is.
 */
function flattenAIObject(obj) {
    const flat = {};

    for (const [key, val] of Object.entries(obj)) {
        // Keep null/undefined as null so all attributes appear in export
        if (val === null || val === undefined) {
            flat[key] = null;
            continue;
        }

        if (Array.isArray(val)) {
            // Array of objects → merge each object's keys
            if (val.length > 0 && typeof val[0] === 'object' && val[0] !== null) {
                for (const item of val) {
                    if (item && typeof item === 'object' && !Array.isArray(item)) {
                        for (const [subKey, subVal] of Object.entries(item)) {
                            flat[subKey] = (subVal !== null && subVal !== undefined && typeof subVal === 'object') ? JSON.stringify(subVal) : subVal;
                        }
                    }
                }
            } else {
                // Array of scalars → join as comma-separated string
                flat[key] = val.filter(v => v !== null && v !== undefined).join(', ');
            }
        } else if (typeof val === 'object') {
            // Nested object → merge its keys directly into flat
            for (const [subKey, subVal] of Object.entries(val)) {
                flat[subKey] = (subVal !== null && subVal !== undefined && typeof subVal === 'object') ? JSON.stringify(subVal) : subVal;
            }
        } else {
            // Scalar value → keep as-is
            flat[key] = val;
        }
    }

    return flat;
}

// Metric units: uppercase and remove space between number and unit
const METRIC_UNITS_RE = /\b(\d+(?:\.\d+)?)\s*(mm|cm|m|km|gm|g|kg|mg|µm|nm|ml|l|kl|hz|khz|mhz|ghz|rpm|kw|mw|w|v|kv|mv|ma|µa|nm|pa|kpa|mpa|bar|°c|°f|n|kn)\b/gi;
function formatMetricUnits(str) {
    return str.replace(METRIC_UNITS_RE, (_, num, unit) => `${num}${unit.toUpperCase()}`);
}

/** Clean nulls: "null" / "NULL" → blank */
function cleanNullValue(val) {
    if (val === null || val === undefined || val === 'null' || val === 'NULL') return '';
    if (Array.isArray(val)) return val.filter(Boolean).join(', ');
    return String(val);
}

/**
 * Map an item + its AI output into the fixed-column values
 */
function getFixedValues(item, ai) {
    const { description1, description2 } = getDescriptions(item);
    // Helper: get AI value, clean nulls to blank
    // Checks multiple key variants (space-separated DB style + underscore AI style)
    const v = (...keys) => {
        for (const k of keys) {
            const val = ai[k];
            if (val !== undefined && val !== null && val !== 'null' && val !== 'NULL' && val !== '') {
                return formatMetricUnits(String(val));
            }
        }
        return '';
    };

    // Classified manufacturer/vendor data from AI source classification
    const mfrs = item.classifiedManufacturers || [];
    const vdrs = item.classifiedVendors || [];

    return [
        item.partNumber || '',                                                                                    // Part Number
        description1,                                                                                             // Description 1
        description2,                                                                                             // Description 2
        item.givenSupplierName || '',                                                                             // given_Supplier Name
        item.givenSupplierPartnumber || '',                                                                       // given_Supplier Partnumber
        item.givenSupplierName2 || '',                                                                            // given_Supplier Name2
        item.givenSupplierPartnumber2 || '',                                                                      // given_Supplier Partnumber2
        item.manufacturerName || '',                                                                              // given_Manufacture Name1
        item.manufacturerNumber || '',                                                                            // given_Manufacturers part number1
        '',                                                                                                       // given_Manufacturers part number2
        '',                                                                                                       // given_Model
        v('NOUN/MODIFIER', 'NOUN_MODIFIER') || item.nounModifier || '',                                           // NOUN/MODIFIER
        v('CLEANSED/ENRICHED', 'CLEANSED_ENRICHED'),                                                              // CLEANSED/ENRICHED
        v('BRAND NAME', 'BRAND_NAME'),                                                                            // BRAND NAME
        mfrs[0]?.name || v('MANUFACTURER NAME 1', 'MANUFACTURER_NAME_1') || item.manufacturerName || '',          // MANUFACTURER NAME 1
        mfrs[0]?.partNumber || v('MANUFACTURER PART NUMBER 1', 'MANUFACTURER_PART_NUMBER_1') || item.manufacturerNumber || '', // MANUFACTURER PART NUMBER 1
        mfrs[0]?.url || v('MANUFACTURER URL 1', 'MANUFACTURER_URL_1') || (mfrs.length > 0 ? item.sourceUrl : '') || '',  // MANUFACTURER URL 1
        mfrs[1]?.name || v('MANUFACTURER NAME 2', 'MANUFACTURER_NAME_2'),                                         // MANUFACTURER NAME 2
        mfrs[1]?.partNumber || v('MANUFACTURER PART NUMBER 2', 'MANUFACTURER_PART_NUMBER_2'),                     // MANUFACTURER PART NUMBER 2
        mfrs[1]?.url || v('MANUFACTURER URL 2', 'MANUFACTURER_URL_2'),                                            // MANUFACTURER URL 2
        mfrs[2]?.name || v('MANUFACTURER NAME 3', 'MANUFACTURER_NAME_3'),                                         // MANUFACTURER NAME 3
        mfrs[2]?.partNumber || v('MANUFACTURER PART NUMBER 3', 'MANUFACTURER_PART_NUMBER_3'),                     // MANUFACTURER PART NUMBER 3
        mfrs[2]?.url || v('MANUFACTURER URL 3', 'MANUFACTURER_URL_3'),                                            // MANUFACTURER URL 3
        vdrs[0]?.name || v('VENDOR NAME', 'VENDOR_NAME', 'VENDOR_NAME_1', 'VENDOR NAME 1') || item.givenSupplierName || '',        // VENDOR NAME
        vdrs[0]?.partNumber || v('VENDOR PART NUMBER', 'VENDOR_PART_NUMBER', 'VENDOR_PART_NUMBER_1', 'VENDOR PART NUMBER 1') || item.givenSupplierPartnumber || '', // VENDOR PART NUMBER
        vdrs[0]?.url || v('VENDOR URL', 'VENDOR_URL', 'VENDOR_URL_1', 'VENDOR URL 1') || (vdrs.length > 0 ? '' : (item.secondaryUrl || '')),  // VENDOR URL
        vdrs[1]?.name || v('VENDOR NAME 2', 'VENDOR_NAME_2'),                                                     // VENDOR NAME 2
        vdrs[1]?.partNumber || v('VENDOR PART NUMBER 2', 'VENDOR_PART_NUMBER_2'),                                  // VENDOR PART NUMBER 2
        vdrs[1]?.url || v('VENDOR URL 2', 'VENDOR_URL_2'),                                                        // VENDOR URL 2
        vdrs[2]?.name || v('VENDOR NAME 3', 'VENDOR_NAME_3'),                                                     // VENDOR NAME 3
        vdrs[2]?.partNumber || v('VENDOR PART NUMBER 3', 'VENDOR_PART_NUMBER_3'),                                  // VENDOR PART NUMBER 3
        vdrs[2]?.url || v('VENDOR URL 3', 'VENDOR_URL_3'),                                                        // VENDOR URL 3
        vdrs[3]?.name || v('VENDOR NAME 4', 'VENDOR_NAME_4'),                                                     // VENDOR NAME 4
        vdrs[3]?.partNumber || v('VENDOR PART NUMBER 4', 'VENDOR_PART_NUMBER_4'),                                  // VENDOR PART NUMBER 4
        vdrs[3]?.url || v('VENDOR URL 4', 'VENDOR_URL_4'),                                                        // VENDOR URL 4
        vdrs[4]?.name || v('VENDOR NAME 5', 'VENDOR_NAME_5'),                                                     // VENDOR NAME 5
        vdrs[4]?.partNumber || v('VENDOR PART NUMBER 5', 'VENDOR_PART_NUMBER_5'),                                  // VENDOR PART NUMBER 5
        vdrs[4]?.url || v('VENDOR URL 5', 'VENDOR_URL_5'),                                                        // VENDOR URL 5
        v('UNUSED_TEXT', 'UNUSED TEXT'),                                                                           // UNUSED_TEXT
    ];
}

// Keys that are already mapped to fixed columns — skip in property pairs
const FIXED_AI_KEYS = new Set([
    'NOUN/MODIFIER', 'NOUN_MODIFIER',
    'CLEANSED/ENRICHED', 'CLEANSED_ENRICHED',
    'BRAND NAME', 'BRAND_NAME',
    'MANUFACTURER NAME 1', 'MANUFACTURER_NAME_1',
    'MANUFACTURER PART NUMBER 1', 'MANUFACTURER_PART_NUMBER_1',
    'MANUFACTURER URL 1', 'MANUFACTURER_URL_1',
    'MANUFACTURER NAME 2', 'MANUFACTURER_NAME_2',
    'MANUFACTURER PART NUMBER 2', 'MANUFACTURER_PART_NUMBER_2',
    'MANUFACTURER URL 2', 'MANUFACTURER_URL_2',
    'MANUFACTURER NAME 3', 'MANUFACTURER_NAME_3',
    'MANUFACTURER PART NUMBER 3', 'MANUFACTURER_PART_NUMBER_3',
    'MANUFACTURER URL 3', 'MANUFACTURER_URL_3',
    'VENDOR NAME', 'VENDOR_NAME', 'VENDOR_NAME_1', 'VENDOR NAME 1',
    'VENDOR PART NUMBER', 'VENDOR_PART_NUMBER', 'VENDOR_PART_NUMBER_1', 'VENDOR PART NUMBER 1',
    'VENDOR URL', 'VENDOR_URL', 'VENDOR_URL_1', 'VENDOR URL 1',
    'VENDOR NAME 2', 'VENDOR_NAME_2',
    'VENDOR PART NUMBER 2', 'VENDOR_PART_NUMBER_2',
    'VENDOR URL 2', 'VENDOR_URL_2',
    'VENDOR NAME 3', 'VENDOR_NAME_3',
    'VENDOR PART NUMBER 3', 'VENDOR_PART_NUMBER_3',
    'VENDOR URL 3', 'VENDOR_URL_3',
    'VENDOR NAME 4', 'VENDOR_NAME_4',
    'VENDOR PART NUMBER 4', 'VENDOR_PART_NUMBER_4',
    'VENDOR URL 4', 'VENDOR_URL_4',
    'VENDOR NAME 5', 'VENDOR_NAME_5',
    'VENDOR PART NUMBER 5', 'VENDOR_PART_NUMBER_5',
    'VENDOR URL 5', 'VENDOR_URL_5',
    'UNUSED_TEXT', 'UNUSED TEXT',
    // META fields — keep out of property columns
    'SOURCE_VERIFICATION_FULL_URL', 'SOURCE_VERIFICATION_URL',
    'DATA_CONFIDENCE_SCORE',
    // Unmapped field — exported as its own 'Unmatched' column
    'UNMAPPED', 'UNMATCHED', 'unmapped',
]);


/**
 * Extract remaining AI properties into PROPERTY/VALUE pairs (up to 30)
 * Includes ALL properties, even those with null/empty values (shown as blank)
 */
function collectPropertyPairs(ai) {
    const pairs = [];
    for (const [key, val] of Object.entries(ai)) {
        if (FIXED_AI_KEYS.has(key)) continue;
        // Include ALL attributes — even null/empty ones show as blank value
        const cleaned = cleanNullValue(val);
        const displayVal = cleaned ? formatMetricUnits(cleaned) : '';
        pairs.push([key.toUpperCase(), displayVal]);
    }
    return pairs;
}

function getPropertyPairs(ai) {
    const pairs = collectPropertyPairs(ai);
    // Pad to exactly MAX_PROPERTIES pairs (empty cells)
    const cells = [];
    for (let i = 0; i < MAX_PROPERTIES; i++) {
        if (i < pairs.length) {
            cells.push(pairs[i][0], pairs[i][1]);
        } else {
            cells.push('', '');
        }
    }
    return cells;
}

// Extra columns appended after properties for review workflow
const EXTRA_HEADERS = ['_Status', '_Source', 'Unmatched'];

/**
 * Build full header row (92 + 3 extra columns)
 */
function buildHeaderRow() {
    const headers = [...FIXED_HEADERS];
    for (let i = 1; i <= MAX_PROPERTIES; i++) {
        headers.push(`PROPERTY${i}`, `VALUE_OF_PROPERTY${i}`);
    }
    headers.push(...EXTRA_HEADERS);
    return headers;
}

/**
 * Build a flat record object from an IndexedDB item.
 * Returns { 'Description 1': '...', 'Description 2': '...', 'NOUN/MODIFIER': '...', ... }
 *
 * This is the SINGLE SOURCE OF TRUTH for mapping items → the 101-column format.
 * Used by both Excel export AND DB save to guarantee identical data.
 *
 * @param {Object} item - An IndexedDB result item
 * @returns {Object} Flat key-value map matching DB column names
 */
export function buildFlatRecord(item) {
    // 1. Parse + enrich AI output (same as Excel export)
    let ai = parseAIOutput(item.chatgptOutput);
    ai = injectManufacturerMeta(ai, item);

    // 2. Build fixed column values (38 values)
    const fixedValues = getFixedValues(item, ai);

    // 3. Build property pair values (60 values)
    const propValues = getPropertyPairs(ai);

    // 4. Build status values (3 values)
    const unmatchedVal = ai['UNMAPPED'] || ai['UNMATCHED'] || ai['unmapped'] || '';
    const extraValues = [
        item.reviewStatus || 'PENDING',
        item.sourceType || 'Online',
        cleanNullValue(unmatchedVal),
    ];

    // 5. Combine into one row array
    const row = [...fixedValues, ...propValues, ...extraValues];

    // 6. Map to column-name keyed object
    const headers = buildHeaderRow();
    const record = {};
    headers.forEach((header, i) => {
        const val = row[i];
        // Store non-empty values; null/empty → null (DB will store NULL)
        record[header] = (val !== undefined && val !== null && val !== '') ? String(val) : null;
    });

    return record;
}

// ── Backend BU export layout ─────────────────────────────────────────
// The active Backend BU type decides the export columns:
//   input columns + Part A columns + Part B property pairs + _Status / _Source / Unmatched.
// Part A labels are matched to our fixed columns ignoring case, spaces and punctuation.
// (The DB save via buildFlatRecord keeps the full fixed column set.)

const EXPORT_INPUT_HEADERS = [
    'Part Number',
    'Description 1',
    'Description 2',
    'given_Supplier Name',
    'given_Supplier Partnumber',
];

// Used when no Backend BU is active: every AI column + all 30 property pairs
const DEFAULT_EXPORT_LAYOUT = {
    name: null,
    partA: FIXED_HEADERS.slice(FIXED_HEADERS.indexOf('NOUN/MODIFIER')),
    partB: Array.from({ length: MAX_PROPERTIES }, (_, i) => [`PROPERTY${i + 1}`, `VALUE_OF_PROPERTY${i + 1}`]).flat(),
};

const normalizeLabel = (label) => String(label || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const FIXED_HEADER_BY_LABEL = new Map(FIXED_HEADERS.map(h => [normalizeLabel(h), h]));

// BU labels that name a fixed column differently
const BU_LABEL_ALIASES = {
    ENRICHEDCLEANSED: 'CLEANSED/ENRICHED',
    BRAND: 'BRAND NAME',
    MANUFACTURERNAME: 'MANUFACTURER NAME 1',
    MANUFACTURERPARTNUMBER: 'MANUFACTURER PART NUMBER 1',
    MANUFACTURERURL: 'MANUFACTURER URL 1',
    ENRICHEDURL: 'MANUFACTURER URL 1',
    ENRICHEDURL1: 'MANUFACTURER URL 1',
    ENRICHEDURL2: 'MANUFACTURER URL 2',
    ENRICHEDURL3: 'MANUFACTURER URL 3',
    VENDORNAME1: 'VENDOR NAME',
    VENDORPARTNUMBER1: 'VENDOR PART NUMBER',
    VENDORURL1: 'VENDOR URL',
    UNUSED: 'UNUSED_TEXT',
};

const resolveFixedHeader = (label) => {
    const key = normalizeLabel(label);
    return FIXED_HEADER_BY_LABEL.get(key) || BU_LABEL_ALIASES[key] || null;
};

/**
 * Turn the /bu-types/active payload into an export layout.
 * Returns null when there is no active BU or it has no columns.
 */
export function buildBuExportLayout(activeBu) {
    const labels = (values) => (values || [])
        .map(v => String(v?.value_text || '').trim())
        .filter(Boolean);
    const partA = labels(activeBu?.valuesA);
    const partB = labels(activeBu?.valuesB);
    if (partA.length === 0 && partB.length === 0) return null;
    return { name: activeBu.type_name || 'Active BU', partA, partB };
}

/**
 * Build the header + cell resolvers for a BU layout.
 * Each resolver takes { fixedByHeader, pairs, ai } and returns the cell value.
 */
function compileBuLayout(layout) {
    const columns = EXPORT_INPUT_HEADERS.map(h => ({ header: h, get: ({ fixedByHeader }) => fixedByHeader[h] }));
    let maxPropertyIndex = 0;

    // Columns the export always places itself — skip them if the BU lists them too
    const placedLabels = new Set([...EXPORT_INPUT_HEADERS, ...EXTRA_HEADERS].map(normalizeLabel));

    for (const label of [...layout.partA, ...layout.partB]) {
        if (placedLabels.has(normalizeLabel(label))) continue;
        const propMatch = label.match(/^(VALUE_OF_)?PROPERTY[\s_]*(\d+)$/i);
        if (propMatch) {
            const index = Number(propMatch[2]) - 1;
            const slot = propMatch[1] ? 1 : 0; // 0 = property name, 1 = value
            maxPropertyIndex = Math.max(maxPropertyIndex, index + 1);
            columns.push({ header: label, get: ({ pairs }) => pairs[index]?.[slot] ?? '' });
            continue;
        }
        const fixedHeader = resolveFixedHeader(label);
        if (fixedHeader) {
            columns.push({ header: label, get: ({ fixedByHeader }) => fixedByHeader[fixedHeader] });
            continue;
        }
        // Not a known column — use a matching AI output key if there is one
        const key = normalizeLabel(label);
        columns.push({
            header: label,
            get: ({ ai }) => {
                const aiKey = Object.keys(ai).find(k => normalizeLabel(k) === key);
                return aiKey ? cleanNullValue(ai[aiKey]) : '';
            },
        });
    }

    // Fixed trailing columns for the review workflow
    columns.push(
        { header: '_Status', get: ({ item }) => item.reviewStatus || 'PENDING' },
        { header: '_Source', get: ({ item }) => item.sourceType || 'Online' },
        {
            header: 'Unmatched',
            get: ({ ai }) => cleanNullValue(ai['UNMAPPED'] || ai['UNMATCHED'] || ai['unmapped'] || ''),
        },
    );
    return { columns, maxPropertyIndex };
}

/**
 * Build all export rows (header + data)
 * Implements Parent-Child merge: if the same part number has both a scraped (Parent)
 * row and a DB (Child) row, missing values in the Child are filled from the Parent.
 *
 * @param {Array} items
 * @param {Object|null} layout - From buildBuExportLayout(); null = all columns
 * @returns {{ rows: Array<Array>, truncatedRows: number }}
 */
function buildExportRows(items, layout = null) {
    const compiled = compileBuLayout(layout || DEFAULT_EXPORT_LAYOUT);
    const headers = compiled.columns.map(c => c.header);
    let truncatedRows = 0;

    // ── Pass 1: Build a "Parent" AI data map from scraped (non-DB) items ──
    // Keyed by MANUFACTURER PART NUMBER 1 (case-insensitive, trimmed)
    const parentMap = new Map();
    for (const item of items) {
        if (item.sourceType === 'DB') continue; // DB items are children, skip
        const ai = parseAIOutput(item.chatgptOutput);
        const pn = (
            ai['MANUFACTURER PART NUMBER 1'] ||
            ai['MANUFACTURER_PART_NUMBER_1'] ||
            item.manufacturerNumber ||
            ''
        ).toString().trim().toLowerCase();
        if (pn) {
            parentMap.set(pn, ai);
        }
    }

    // ── Pass 2: Build export rows, merging DB children with their Parents ──
    const dataRows = items.map(item => {
        let ai = parseAIOutput(item.chatgptOutput);

        // If this is a DB-sourced item, merge with Parent data to fill gaps
        if (item.sourceType === 'DB' && parentMap.size > 0) {
            const pn = (
                ai['MANUFACTURER PART NUMBER 1'] ||
                ai['MANUFACTURER_PART_NUMBER_1'] ||
                item.manufacturerNumber ||
                ''
            ).toString().trim().toLowerCase();
            const parentAI = pn ? parentMap.get(pn) : null;
            if (parentAI) {
                // Merge: Parent values fill in where Child has NULL/empty
                const merged = { ...parentAI }; // Start with all Parent data
                for (const [key, val] of Object.entries(ai)) {
                    // Child values override Parent only when non-empty
                    if (val !== null && val !== undefined && val !== '' && val !== 'null' && val !== 'NULL') {
                        merged[key] = val;
                    }
                }
                ai = merged;
            }
        }

        // Inject manufacturer/vendor metadata from item's classified fields into AI output
        // This ensures Google URL scraping step data (mfr name, URL, PN) appears in the export
        ai = injectManufacturerMeta(ai, item);

        const fixed = getFixedValues(item, ai);
        const fixedByHeader = Object.fromEntries(FIXED_HEADERS.map((h, i) => [h, fixed[i] ?? '']));
        const pairs = collectPropertyPairs(ai);
        // Properties beyond the layout's last PROPERTY column have no slot in the export
        if (pairs.slice(compiled.maxPropertyIndex).some(([, value]) => value)) {
            truncatedRows++;
        }
        return compiled.columns.map(c => c.get({ item, fixedByHeader, pairs, ai }) ?? '');
    });
    return { rows: [headers, ...dataRows], truncatedRows };
}

/**
 * Export items as CSV download
 */
export function exportToCSV(items, layout = null) {
    if (!items || items.length === 0) return { truncatedRows: 0 };

    const { rows, truncatedRows } = buildExportRows(items, layout);
    const csvContent = rows
        .map(row =>
            row.map(cell => {
                const cellStr = String(cell || '');
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(',')
        )
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `banyan-results-${timestamp()}.csv`);
    return { truncatedRows };
}

/**
 * Export items as true XLSX download (uses xlsx library)
 */
export function exportToExcelXLSX(items, layout = null) {
    if (!items || items.length === 0) return { truncatedRows: 0 };

    const { rows, truncatedRows } = buildExportRows(items, layout);
    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Auto-size columns (cap at 40 chars width)
    const headers = rows[0];
    ws['!cols'] = headers.map((h, i) => ({
        wch: Math.min(
            40,
            Math.max(
                String(h).length + 2,
                ...rows.slice(1, 20).map(r => String(r[i] || '').length),
                10
            )
        )
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Results');
    XLSX.writeFile(wb, `banyan-results-${timestamp()}.xlsx`);
    return { truncatedRows };
}

// Legacy named export for backward compatibility
export const exportToExcel = exportToExcelXLSX;

// ── Helpers ──────────────────────────────────────────────────────────
function timestamp() {
    return new Date().toISOString().slice(0, 10);
}

function downloadBlob(blob, filename) {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * @deprecated Use convertToCSV via exportToCSV instead
 */
export function convertToCSV(items) {
    const { rows } = buildExportRows(items);
    return rows
        .map(row =>
            row.map(cell => {
                const cellStr = String(cell || '');
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                }
                return cellStr;
            }).join(',')
        )
        .join('\n');
}

export function downloadCSV(csvContent, filename = 'results.csv') {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, filename);
}

/**
 * Parse an imported Excel file (ArrayBuffer) and return items suitable for IndexedDB update.
 * Reconstructs chatgptOutput JSON from PROPERTY/VALUE columns + fixed AI columns.
 * Items are matched by Part Number, falling back to given_Supplier Partnumber.
 * @param {ArrayBuffer} buffer - Excel file buffer
 * @returns {Array<Object>} parsed items
 */
export function parseImportedExcel(buffer) {
    const wb = XLSX.read(buffer, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
    if (rows.length < 2) return [];

    const headers = rows[0].map(h => String(h || '').trim());
    const colIndex = (name) => headers.indexOf(name);

    const results = [];

    for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const cell = (name) => {
            const i = colIndex(name);
            return i >= 0 ? (row[i] ?? '') : '';
        };

        // Reconstruct the AI JSON from fixed + property columns
        const aiObj = {};

        // Fixed AI columns
        const fixedAiMappings = [
            'NOUN/MODIFIER', 'CLEANSED/ENRICHED', 'BRAND NAME',
            'MANUFACTURER NAME 1', 'MANUFACTURER PART NUMBER 1', 'MANUFACTURER URL 1',
            'MANUFACTURER NAME 2', 'MANUFACTURER PART NUMBER 2', 'MANUFACTURER URL 2',
            'MANUFACTURER NAME 3', 'MANUFACTURER PART NUMBER 3', 'MANUFACTURER URL 3',
            'VENDOR NAME', 'VENDOR PART NUMBER', 'VENDOR URL',
            'VENDOR NAME 2', 'VENDOR PART NUMBER 2', 'VENDOR URL 2',
            'VENDOR NAME 3', 'VENDOR PART NUMBER 3', 'VENDOR URL 3',
            'VENDOR NAME 4', 'VENDOR PART NUMBER 4', 'VENDOR URL 4',
            'VENDOR NAME 5', 'VENDOR PART NUMBER 5', 'VENDOR URL 5',
            'UNUSED_TEXT',
        ];
        for (const col of fixedAiMappings) {
            const v = cell(col);
            if (v !== '' && v !== null && v !== undefined) aiObj[col] = String(v);
        }

        // Property pairs
        for (let i = 1; i <= MAX_PROPERTIES; i++) {
            const propName = cell(`PROPERTY${i}`);
            const propVal = cell(`VALUE_OF_PROPERTY${i}`);
            if (propName && String(propName).trim()) {
                aiObj[String(propName).trim()] = String(propVal || '');
            }
        }

        const partNumber = String(cell('Part Number') || '').trim();
        const supplierPartNumber = String(cell('given_Supplier Partnumber') || '').trim();
        if (!partNumber && !supplierPartNumber) continue; // skip rows with no identifier

        const item = {
            description1: String(cell('Description 1') || cell('Description') || ''),
            description2: String(cell('Description 2') || ''),
            givenSupplierName: String(cell('given_Supplier Name') || ''),
            partNumber,
            givenSupplierPartnumber: supplierPartNumber,
            nounModifier: String(cell('NOUN/MODIFIER') || aiObj['NOUN/MODIFIER'] || ''),
            chatgptOutput: JSON.stringify(aiObj),
            reviewStatus: String(cell('_Status') || 'PENDING').toUpperCase(),
            sourceType: String(cell('_Source') || 'Online'),
        };

        // If Unmatched column has content, include it in the AI output
        const unmatchedVal = cell('Unmatched');
        if (unmatchedVal && String(unmatchedVal).trim()) {
            aiObj['UNMAPPED'] = String(unmatchedVal).trim();
            item.chatgptOutput = JSON.stringify(aiObj);
        }

        results.push(item);
    }

    return results;
}

export default {
    convertToCSV,
    downloadCSV,
    exportToCSV,
    exportToExcelXLSX,
    exportToExcel,
    parseImportedExcel,
    buildFlatRecord,
};
